import { BeakerIcon } from "@heroicons/react/outline";
import { Loader2, PlusIcon } from "lucide-react";

import { ASSERTION_REQUIRED_TOOLTIP, SIGNAL_SAMPLE_REQUEST_LIMIT } from "./constants";

import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";

interface ControlBarProps {
  collapsed?: boolean;
  requestLogCount: number;
  hasAssertion: boolean;
  isTestingAll: boolean;
  isTestPending: boolean;
  hasTestActivity: boolean;
  testStats: {
    running: number;
    trueCount: number;
    falseCount: number;
  };
  onAddRequests: () => void;
  onTestAll: () => void;
}

const ControlBar = ({
  collapsed = false,
  requestLogCount,
  hasAssertion,
  isTestingAll,
  isTestPending,
  hasTestActivity,
  testStats,
  onAddRequests,
  onTestAll,
}: ControlBarProps) => {
  const testAllLabel = requestLogCount > 0 ? `Test all ${requestLogCount}` : "Test all";
  const selectionSummary =
    requestLogCount > 0
      ? `${requestLogCount} request${requestLogCount === 1 ? "" : "s"} selected`
      : `Up to ${SIGNAL_SAMPLE_REQUEST_LIMIT} requests`;

  const statsContent = hasTestActivity ? (
    <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400">
      {testStats.running > 0 ? (
        <span className="inline-flex items-center gap-1 text-gray-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          {testStats.running} running
        </span>
      ) : null}
      {testStats.running > 0 && (testStats.trueCount > 0 || testStats.falseCount > 0) ? "·" : null}
      {testStats.trueCount > 0 ? (
        <span className="text-gray-500">{testStats.trueCount} true</span>
      ) : null}
      {testStats.trueCount > 0 && testStats.falseCount > 0 ? "·" : null}
      {testStats.falseCount > 0 ? (
        <span className="text-gray-500">{testStats.falseCount} false</span>
      ) : null}
    </div>
  ) : null;

  const actionButtons = (
    <div className="flex shrink-0 items-center gap-2">
      <Button type="button" size="sm" variant="outline" onClick={onAddRequests}>
        <PlusIcon className="mr-1.5 h-4 w-4" />
        Add Requests
      </Button>
      <BasicTooltip content={ASSERTION_REQUIRED_TOOLTIP} shouldShow={!hasAssertion}>
        <span className="inline-flex shrink-0">
          <Button
            type="button"
            size="sm"
            onClick={onTestAll}
            disabled={!hasAssertion || requestLogCount === 0 || isTestingAll || isTestPending}
          >
            {testAllLabel}
          </Button>
        </span>
      </BasicTooltip>
    </div>
  );

  if (collapsed) {
    return (
      <div className="flex h-full w-full shrink-0 items-center justify-between gap-3 px-4 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          <BeakerIcon className="h-4 w-4 shrink-0 text-blue-600" />
          <span className="truncate text-sm font-medium text-gray-900">
            Test on selected requests
          </span>
          {statsContent}
        </div>
        {actionButtons}
      </div>
    );
  }

  return (
    <div className="flex shrink-0 items-start justify-between gap-4 px-5 pb-3 pt-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <div className="flex items-center gap-2">
            <BeakerIcon className="h-5 w-5 text-blue-600" />
            <h2 className="text-base font-semibold text-gray-900">Test on selected requests</h2>
          </div>
          {statsContent}
        </div>
        <p className="mt-1 text-sm text-gray-500">{selectionSummary}</p>
      </div>
      {actionButtons}
    </div>
  );
};

export default ControlBar;
